import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { usePrototype } from './PrototypeContext'
import { DEFAULT_PROGRESSION as P } from '../../rules/progression/config'
import { generatedKillsToWin } from '../../rules/progression/warSetup'
import '../LobbyScreen.css'
import './prototype.css'

// Go to war: who to fight (the Bot or a friend), then which Hive: your own,
// or a test war on generated veteran Hives of a chosen number of structures
// (both sides get one; no rewards), then for a friend how (async or sync).
// Then start: a war with the Bot opens, a challenge opens its waiting room.
// A code from a friend can be entered at the bottom at any time.
const FOES = [
    { key: 'bot', label: 'The Bot', sub: 'A war right now.' },
    { key: 'human', label: 'A friend', sub: 'Send them a code.' },
]
const MODES = [
    { key: 'async', label: 'Async', sub: 'Take turns any time. Each turn has a deadline.' },
    { key: 'sync', label: 'Sync', sub: 'Play live. Each side has a clock.' },
]
const HIVES = [
    { key: 'mine', label: 'My Hive', sub: 'The Hive you built.' },
    { key: 'generated', label: 'Generated', sub: 'A veteran Hive for both sides. No rewards.' },
]
const MAX_STRUCTURES = P.inputs.maxStructuresHardCap
const DEFAULT_STRUCTURES = Math.min(7, MAX_STRUCTURES)

function Choice({ options, value, onChange, disabled }) {
    return (
        <div className="pt-choice" role="radiogroup">
            {options.map(o => (
                <button key={o.key} role="radio" aria-checked={value === o.key} disabled={disabled}
                    className={value === o.key ? 'pt-choice__opt pt-choice__opt--on' : 'pt-choice__opt'}
                    onClick={() => onChange(o.key)}>
                    <span className="pt-choice__label">{o.label}</span>
                    <span className="pt-choice__sub">{o.sub}</span>
                </button>
            ))}
        </div>
    )
}

export default function WarMenu() {
    const { notice, setNotice, pendingInvite, setPendingInvite, startBotWar, createChallenge, acceptChallenge, goHome } = usePrototype()
    const [busy, setBusy] = useState(false)
    const [foe, setFoe] = useState(null)
    const [mode, setMode] = useState(null)
    const [hive, setHive] = useState(null)
    const [structures, setStructures] = useState(DEFAULT_STRUCTURES)
    // An invite link's code fills the box once; home stops sending the player here.
    const [code, setCode] = useState(pendingInvite ?? '')
    useEffect(() => { if (pendingInvite) setPendingInvite(null) }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const run = (fn) => async (...args) => {
        setBusy(true)
        try { await fn(...args) } finally { setBusy(false) }
    }
    const generated = hive === 'generated' ? structures : null
    const start = run(() => (foe === 'bot' ? startBotWar(generated) : createChallenge(mode, generated)))
    const step = (d) => setStructures(n => Math.max(1, Math.min(MAX_STRUCTURES, n + d)))
    // Each question shows once the one before it is answered.
    const ready = !!hive && (foe === 'bot' || (foe === 'human' && !!mode))
    const kills = generatedKillsToWin(structures)

    return (
        <div className="pt-screen">
            <header className="pt-menu__head">
                <button className="pt-back" onClick={goHome} aria-label="Back to home">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <h1 className="pt-menu__title">Go to war</h1>
            </header>

            {notice && <p className="lobby-error pt-notice" onClick={() => setNotice(null)}>{notice}</p>}

            <div className="lobby-actions">
                <div className="lobby-card">
                    <p className="lobby-label">Who do you fight?</p>
                    <Choice options={FOES} value={foe} onChange={setFoe} disabled={busy} />

                    {foe && (
                        <>
                            <p className="lobby-label pt-menu__step">Which Hive?</p>
                            <Choice options={HIVES} value={hive} onChange={setHive} disabled={busy} />
                            {hive === 'generated' && (
                                <div className="pt-stepper">
                                    <span className="pt-stepper__label">Structures</span>
                                    <button className="pt-stepper__btn" disabled={busy || structures <= 1} onClick={() => step(-1)} aria-label="Fewer structures">−</button>
                                    <span className="pt-stepper__n">{structures}</span>
                                    <button className="pt-stepper__btn" disabled={busy || structures >= MAX_STRUCTURES} onClick={() => step(1)} aria-label="More structures">+</button>
                                    <span className="pt-stepper__note">{kills} kill{kills === 1 ? '' : 's'} to win</span>
                                </div>
                            )}
                        </>
                    )}

                    {foe === 'human' && hive && (
                        <>
                            <p className="lobby-label pt-menu__step">How do you play?</p>
                            <Choice options={MODES} value={mode} onChange={setMode} disabled={busy} />
                        </>
                    )}

                    <button className="lobby-btn-primary pt-menu__start" disabled={busy || !ready} onClick={start}>
                        {foe === 'human' ? 'Create challenge' : 'Start war'}
                    </button>
                </div>

                <div className="lobby-card">
                    <p className="lobby-label">Have a code?</p>
                    <form onSubmit={(e) => { e.preventDefault(); run(acceptChallenge)(code.trim()) }}>
                        <input className="lobby-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="A7K2P9" maxLength={6} autoComplete="off" autoCapitalize="characters" spellCheck={false} />
                        <button className="lobby-btn-primary" type="submit" disabled={busy || code.trim().length < 6}>Accept</button>
                    </form>
                </div>
            </div>
        </div>
    )
}
